import { uniq } from 'lodash';
import type { EpisodeCategory } from '../../../../../../common/enums/episodeCategory';
import type { IEpisodeCategory } from '../../../../../../common/interfaces/episodeCategory';
import type { IServiceStartingPoint } from '../../../../../../common/interfaces/serviceWorkflow';
import type { AbortableFetch, IFetchResult } from '../../../../../store/fetch';
import type { IService } from '../newProcessInterfaces';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { getUniqueByKey } from '../../../../../../common/utils/getUniqueByKey';
import { UrlBuilder } from '../../../../../../common/utils/urlBuilder';
import { getJson as nonAbortableGetJson } from '../../../../../store/fetch';

export class NewProcessApi {
    private getJson: (url: string) => Promise<IFetchResult>;

    public constructor(private abortableFetchContainer?: AbortableFetch) {
        this.getJson = async (url) => {
            if (this.abortableFetchContainer) {
                return await this.abortableFetchContainer.getJson(url);
            } else {
                return await nonAbortableGetJson(url);
            }
        };
    }

    public async loadServiceTypes(
        patientId?: string,
        isInFlexCarePath?: boolean,
        isInOldCarePath?: boolean
    ): Promise<{
        services: IService[];
        serviceTypes: ServiceType[];
    }> {
        let serviceTypes: ServiceType[] = [];

        const consumerServices = (await this.loadConsumerServices()).map((service) => {
            if (service.serviceType === ServiceType.examination) {
                return { ...service, serviceType: ServiceType.inquiry };
            } else {
                return service;
            }
        });

        const withProcedureServices = await this.loadWithProcedureServices();
        const withStartingPoints = await this.loadWithStartingPoints(patientId);

        let services = [];
        if (isInFlexCarePath) {
            services = withStartingPoints;
        } else if (isInOldCarePath) {
            services = [...consumerServices, ...withProcedureServices];
        } else {
            services = [...consumerServices, ...withProcedureServices, ...withStartingPoints];
        }

        if (services.length > 0) {
            serviceTypes = this.getUniqueServiceTypes(services);
        }

        return { serviceTypes, services };
    }

    public async loadEpisodeCategories(): Promise<IEpisodeCategory[]> {
        const urlBuilder = new UrlBuilder<IEpisodeCategoriesUrlParameters>('/api/episodeCategories');
        const url = urlBuilder
            .add('isActive', true)
            .add('isConsumer', true)
            .add('isProvider', true)
            .add('isReader', true)
            .add('isServiceEmployment', true)
            .build();

        const response = await this.getJson(url);
        if (response.json) {
            return response.json.items;
        } else {
            return [];
        }
    }

    public async createServiceMap(patientId?: string, isInFlexCarePath?: boolean, isInOldCarePath?: boolean) {
        const { serviceTypes, services } = await this.loadServiceTypes(patientId, isInFlexCarePath, isInOldCarePath);

        const serviceMap: ServiceMapType = {};
        const episodeCategories = await this.loadEpisodeCategories();

        for (const serviceType of serviceTypes) {
            serviceMap[serviceType] = {};
            const uniqueEpisodeCategoryIdsByServiceType = uniq(
                services
                    .filter((service) => service.serviceType === serviceType)
                    .map((service) => service.episodeCategoryId)
            );
            const uniqueEpisodeCategoriesByServiceType = episodeCategories.filter((episodeCategory) =>
                uniqueEpisodeCategoryIdsByServiceType.includes(episodeCategory.id)
            );

            for (const episodeCategory of uniqueEpisodeCategoriesByServiceType) {
                serviceMap[serviceType]![episodeCategory.name] = services.filter((service) => {
                    return service.serviceType === serviceType && service.episodeCategoryId === episodeCategory.id;
                });
            }
        }

        return serviceMap;
    }

    private async loadWithProcedureServices(): Promise<IService[]> {
        const urlBuilder = new UrlBuilder<IServicesListUrlParameters>('/api/servicesList');
        const url = urlBuilder.add('showBiometrician', true).add('showRequestCreator', true).build();

        const response = await this.getJson(url);

        return response.json.items;
    }

    private async loadConsumerServices(): Promise<IService[]> {
        const urlBuilder = new UrlBuilder<IServicesListUrlParameters>('/api/servicesList');
        const url = urlBuilder.add('showConsumer', true).add('showRequestCreator', true).build();

        const response = await this.getJson(url);

        return response.json.items;
    }

    private async loadWithStartingPoints(patientId?: string): Promise<IService[]> {
        const urlBuilder = new UrlBuilder('/api/services/serviceStartingPoints');
        if (patientId) {
            urlBuilder.add({
                patientId: patientId || null,
            });
        }
        const url = urlBuilder.build();
        const response = await this.getJson(url);
        const startingPoints: IServiceStartingPoint[] = response.json;
        const services: IService[] = [];
        for (const startingPoint of startingPoints) {
            let service = services.find((s) => s.id === startingPoint.service.id);
            if (!service) {
                service = {
                    ...startingPoint.service,
                    episodeCategoryId: startingPoint.service.episodeCategory.id,
                    startingPoints: [],
                    useAppointment: false,
                    withProcedure: false,
                };
                services.push(service);
            }
            service.startingPoints!.push(startingPoint);
        }

        return services;
    }

    private getUniqueServiceTypes(services: IService[]) {
        return getUniqueByKey(services, 'serviceType');
    }
}

interface IServicesListUrlParameters {
    episodeCategory: EpisodeCategory;
    hasNoEpisodeCategory: boolean;
    isStartService: boolean;
    servicetype: ServiceType;
    showBiometrician: boolean;
    showConsumer: boolean;
    showRequestCreator: boolean;
}

interface IEpisodeCategoriesUrlParameters {
    isActive: boolean;
    isConsumer: boolean;
    isProvider: boolean;
    isReader: boolean;
    isServiceEmployment: boolean;
    isSupervisor: boolean;
    patientId: string;
}

export type ServiceMapType = {
    [serviceType in ServiceType]?: { [episodeCategory in EpisodeCategory]?: IService[] };
};

export type EpisodeCategoryServicesMapType = {
    [serviceType in ServiceType]?: IService[];
};

export type EpisodeCategoryMapType = {
    [category in EpisodeCategory]?: EpisodeCategoryServicesMapType;
};
